import React, { createContext, useContext, useState, useCallback } from 'react';

interface Company {
  legalName: string;
  rfc: string;
  website: string;
  logoUrl: string | null;
  customScript?: string;
}

interface SystemInfo {
  tenantId: string;
  tenantName: string;
  createdAt: string;
  totalActiveUsers: number;
}

interface User {
  id: number;
  fullName: string;
  email: string;
  username: string;
  roleId: number;
  roleName: string;
  emailVerified: boolean;
  isActive: boolean;
  createdAt: string;
}

interface Role {
  id: number;
  name: string;
}

interface Alumno {
  id: number;
  numeroIdentificacionUnico: string;
  numeroReferenciaExterna: string | null;
  nombre: string;
  apellidoPaterno: string;
  apellidoMaterno: string | null;
  email: string | null;
  telefono: string | null;
  estatus: 'aspirante' | 'aceptado' | 'activo' | 'egresado' | 'baja' | 'baja_temporal' | 'baja_definitiva' | 'reincorporado' | 'baja_administrativa' | 'baja_voluntaria' | 'baja_por_inasistencia' | 'en_espera';
  carrera: string | null;
  curso: string | null;
  programId?: number | null;
  groupId?: number | null;
  fechaCreacion: string;
  fechaActualizacion: string;
}

interface ApiKey {
  id: number;
  name: string;
  key: string;
  isActive: boolean;
  createdAt: string;
  lastUsed?: string;
}

interface CacheData {
  company: Company | null;
  systemInfo: SystemInfo | null;
  users: User[] | null;
  roles: Role[] | null;
  alumnos: Alumno[] | null;
  apiKeys: ApiKey[] | null;
}

interface DataCacheContextType {
  // Data getters with cache
  getCompanyData: () => Promise<Company | null>;
  getSystemInfo: () => Promise<SystemInfo | null>;
  getUsersData: () => Promise<User[]>;
  getRolesData: () => Promise<Role[]>;
  getAlumnosData: () => Promise<Alumno[]>;
  getApiKeysData: () => Promise<ApiKey[]>;
  
  // Write operations  
  createAlumno: (alumnoData: any) => Promise<any>;
  createApiKey: (name: string) => Promise<any>;
  
  // Cache management
  invalidateCache: (key?: keyof CacheData) => void;
  clearAllCache: () => void;
  clearTenantCache: (tenantId?: string) => void;
  
  // Direct data access (for UI reactivity)
  cachedData: CacheData;
}

const DataCacheContext = createContext<DataCacheContextType | undefined>(undefined);

// Cache with timestamps to handle expiration (scoped by tenant)
const cache = new Map<string, {
  company: { data: Company | null, timestamp: number },
  systemInfo: { data: SystemInfo | null, timestamp: number },
  users: { data: User[] | null, timestamp: number },
  roles: { data: Role[] | null, timestamp: number },
  alumnos: { data: Alumno[] | null, timestamp: number },
  apiKeys: { data: ApiKey[] | null, timestamp: number },
}>();

// Cache duration: 5 minutes
const CACHE_DURATION = 5 * 60 * 1000;

// Active requests to prevent duplicate calls (scoped by tenant)
const activeRequests = new Map<string, Promise<any>>();

// Get current tenant scope from system info or auth context
let currentTenantScope: string | null = null;

// Function to set tenant scope (called from auth context)
export const setTenantScope = (tenantId: string | null) => {
  currentTenantScope = tenantId;
};

export function DataCacheProvider({ children }: { children: React.ReactNode }) {
  const [cachedData, setCachedData] = useState<CacheData>({
    company: null,
    systemInfo: null,
    users: null,
    roles: null,
    alumnos: null,
    apiKeys: null,
  });

  const updateCachedData = (key: keyof CacheData, data: any) => {
    setCachedData(prev => ({ ...prev, [key]: data }));
  };

  const getTenantScope = (): string | null => {
    // Return current tenant scope or null if not authenticated
    return currentTenantScope;
  };

  const getTenantCache = (tenantScope: string | null) => {
    // Don't cache if no tenant scope - security critical
    if (!tenantScope) return null;
    
    if (!cache.has(tenantScope)) {
      cache.set(tenantScope, {
        company: { data: null, timestamp: 0 },
        systemInfo: { data: null, timestamp: 0 },
        users: { data: null, timestamp: 0 },
        roles: { data: null, timestamp: 0 },
        alumnos: { data: null, timestamp: 0 },
        apiKeys: { data: null, timestamp: 0 },
      });
    }
    return cache.get(tenantScope)!;
  };

  const isExpired = (timestamp: number): boolean => {
    return Date.now() - timestamp > CACHE_DURATION;
  };

  const getCompanyData = useCallback(async (): Promise<Company | null> => {
    const tenantScope = getTenantScope();
    if (!tenantScope) {
      console.warn('No tenant scope available for company data');
      return null;
    }
    
    const tenantCache = getTenantCache(tenantScope);
    const cacheKey = `${tenantScope}:company`;
    
    // Check cache first
    if (tenantCache && tenantCache.company.data && !isExpired(tenantCache.company.timestamp)) {
      return tenantCache.company.data;
    }

    // Check if request is already in progress
    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey);
    }

    // Make the request
    const request = async () => {
      try {
        const response = await fetch('/api/company', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          // Set tenant scope from company data if available and not set
          if (data && data.tenantId && !currentTenantScope) {
            currentTenantScope = data.tenantId;
          }
          const currentCache = getTenantCache(getTenantScope());
          if (currentCache) {
            currentCache.company = { data, timestamp: Date.now() };
            updateCachedData('company', data);
          }
          return data;
        }
        return null;
      } catch (error) {
        console.error('Error fetching company data:', error);
        return null;
      } finally {
        activeRequests.delete(cacheKey);
      }
    };

    const requestPromise = request();
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const getSystemInfo = useCallback(async (): Promise<SystemInfo | null> => {
    const tenantScope = getTenantScope();
    if (!tenantScope) {
      console.warn('No tenant scope for system info');
      return null;
    }
    
    const tenantCache = getTenantCache(tenantScope)!;
    const cacheKey = `${tenantScope}:systemInfo`;
    
    if (tenantCache.systemInfo.data && !isExpired(tenantCache.systemInfo.timestamp)) {
      return tenantCache.systemInfo.data;
    }

    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey);
    }

    const request = async () => {
      try {
        const response = await fetch('/api/system/info', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          // Set tenant scope from system info
          if (data && data.tenantId && !currentTenantScope) {
            currentTenantScope = data.tenantId;
          }
          const currentCache = getTenantCache(getTenantScope());
          if (currentCache) {
            currentCache.systemInfo = { data, timestamp: Date.now() };
            updateCachedData('systemInfo', data);
          }
          return data;
        }
        return null;
      } catch (error) {
        console.error('Error fetching system info:', error);
        return null;
      } finally {
        activeRequests.delete(cacheKey);
      }
    };

    const requestPromise = request();
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const getUsersData = useCallback(async (): Promise<User[]> => {
    const tenantScope = getTenantScope();
    if (!tenantScope) {
      console.warn('No tenant scope for users');
      return [];
    }
    
    const tenantCache = getTenantCache(tenantScope);
    if (!tenantCache) return [];
    
    const cacheKey = `${tenantScope}:users`;
    
    if (tenantCache.users.data && !isExpired(tenantCache.users.timestamp)) {
      return tenantCache.users.data;
    }

    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey);
    }

    const request = async () => {
      try {
        const response = await fetch('/api/users', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          const currentCache = getTenantCache(getTenantScope());
          if (currentCache) {
            currentCache.users = { data, timestamp: Date.now() };
            updateCachedData('users', data);
          }
          return data;
        }
        return [];
      } catch (error) {
        console.error('Error fetching users:', error);
        return [];
      } finally {
        activeRequests.delete(cacheKey);
      }
    };

    const requestPromise = request();
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const getRolesData = useCallback(async (): Promise<Role[]> => {
    const tenantScope = getTenantScope();
    if (!tenantScope) {
      console.warn('No tenant scope for roles');
      return [];
    }
    
    const tenantCache = getTenantCache(tenantScope);
    if (!tenantCache) return [];
    
    const cacheKey = `${tenantScope}:roles`;
    
    if (tenantCache.roles.data && !isExpired(tenantCache.roles.timestamp)) {
      return tenantCache.roles.data;
    }

    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey);
    }

    const request = async () => {
      try {
        const response = await fetch('/api/roles', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          const currentCache = getTenantCache(getTenantScope());
          if (currentCache) {
            currentCache.roles = { data, timestamp: Date.now() };
            updateCachedData('roles', data);
          }
          return data;
        }
        return [];
      } catch (error) {
        console.error('Error fetching roles:', error);
        return [];
      } finally {
        activeRequests.delete(cacheKey);
      }
    };

    const requestPromise = request();
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const getAlumnosData = useCallback(async (): Promise<Alumno[]> => {
    const tenantScope = getTenantScope();
    if (!tenantScope) {
      console.warn('No tenant scope for alumnos');
      return [];
    }
    
    const tenantCache = getTenantCache(tenantScope);
    if (!tenantCache) return [];
    
    const cacheKey = `${tenantScope}:alumnos`;
    
    if (tenantCache.alumnos.data && !isExpired(tenantCache.alumnos.timestamp)) {
      return tenantCache.alumnos.data;
    }

    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey);
    }

    const request = async () => {
      try {
        const response = await fetch('/api/alumnos?limit=100&page=1', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          const alumnos = data.alumnos || [];
          const currentCache = getTenantCache(getTenantScope());
          if (currentCache) {
            currentCache.alumnos = { data: alumnos, timestamp: Date.now() };
            updateCachedData('alumnos', alumnos);
          }
          return alumnos;
        }
        return [];
      } catch (error) {
        console.error('Error fetching alumnos:', error);
        return [];
      } finally {
        activeRequests.delete(cacheKey);
      }
    };

    const requestPromise = request();
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const getApiKeysData = useCallback(async (): Promise<ApiKey[]> => {
    const tenantScope = getTenantScope();
    if (!tenantScope) {
      console.warn('No tenant scope for API keys');
      return [];
    }
    
    const tenantCache = getTenantCache(tenantScope);
    if (!tenantCache) return [];
    
    const cacheKey = `${tenantScope}:apiKeys`;
    
    if (tenantCache.apiKeys.data && !isExpired(tenantCache.apiKeys.timestamp)) {
      return tenantCache.apiKeys.data;
    }

    if (activeRequests.has(cacheKey)) {
      return activeRequests.get(cacheKey);
    }

    const request = async () => {
      try {
        const response = await fetch('/api/apikeys', {
          credentials: 'include',
        });
        
        if (response.ok) {
          const data = await response.json();
          const apiKeys = data.apiKeys || [];
          const currentCache = getTenantCache(getTenantScope());
          if (currentCache) {
            currentCache.apiKeys = { data: apiKeys, timestamp: Date.now() };
            updateCachedData('apiKeys', apiKeys);
          }
          return apiKeys;
        }
        return [];
      } catch (error) {
        console.error('Error fetching API keys:', error);
        return [];
      } finally {
        activeRequests.delete(cacheKey);
      }
    };

    const requestPromise = request();
    activeRequests.set(cacheKey, requestPromise);
    return requestPromise;
  }, []);

  const invalidateCache = useCallback((key?: keyof CacheData) => {
    const tenantScope = getTenantScope();
    if (!tenantScope) return;
    
    const tenantCache = getTenantCache(tenantScope);
    if (!tenantCache) return;
    
    if (key) {
      tenantCache[key] = { data: null, timestamp: 0 };
      updateCachedData(key, null);
    } else {
      // Invalidate all cache for current tenant
      tenantCache.company = { data: null, timestamp: 0 };
      tenantCache.systemInfo = { data: null, timestamp: 0 };
      tenantCache.users = { data: null, timestamp: 0 };
      tenantCache.roles = { data: null, timestamp: 0 };
      tenantCache.alumnos = { data: null, timestamp: 0 };
      tenantCache.apiKeys = { data: null, timestamp: 0 };
      setCachedData({
        company: null,
        systemInfo: null,
        users: null,
        roles: null,
        alumnos: null,
        apiKeys: null,
      });
    }
  }, []);

  const clearAllCache = useCallback(() => {
    // Clear all cache for all tenants
    cache.clear();
    activeRequests.clear();
    currentTenantScope = null;
    setCachedData({
      company: null,
      systemInfo: null,
      users: null,
      roles: null,
      alumnos: null,
      apiKeys: null,
    });
  }, []);

  const clearTenantCache = useCallback((tenantId?: string) => {
    const targetTenant = tenantId || getTenantScope();
    if (!targetTenant) return; // Guard to prevent null cache.delete
    
    cache.delete(targetTenant);
    // Clear active requests for this tenant
    for (const [key] of activeRequests) {
      if (key.startsWith(`${targetTenant}:`)) {
        activeRequests.delete(key);
      }
    }
    if (targetTenant === getTenantScope()) {
      setCachedData({
        company: null,
        systemInfo: null,
        users: null,
        roles: null,
        alumnos: null,
        apiKeys: null,
      });
    }
  }, []);

  // Create Alumno function
  const createAlumno = async (alumnoData: any) => {
    const response = await fetch('/api/alumnos', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      credentials: 'include',
      body: JSON.stringify(alumnoData),
    });

    if (!response.ok) {
      const errorData = await response.json();
      const err: any = new Error(errorData.error || 'Error al crear alumno');
      if (errorData.fields) err.fieldMap = errorData.fields;
      throw err;
    }

    // Invalidate cache after creation
    invalidateCache('alumnos');
    return response.json();
  };

  // Create API Key function
  const createApiKey = async (name: string) => {
    const response = await fetch('/api/apikeys', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ name: name.trim() })
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.error || 'Error al crear API key');
    }

    // Invalidate cache after creation
    invalidateCache('apiKeys');
    return response.json();
  };

  const value = {
    getCompanyData,
    getSystemInfo,
    getUsersData,
    getRolesData,
    getAlumnosData,
    getApiKeysData,
    createAlumno,
    createApiKey,
    invalidateCache,
    clearAllCache,
    clearTenantCache,
    cachedData,
  };

  return (
    <DataCacheContext.Provider value={value}>
      {children}
    </DataCacheContext.Provider>
  );
}

export function useDataCache() {
  const context = useContext(DataCacheContext);
  if (context === undefined) {
    throw new Error('useDataCache must be used within a DataCacheProvider');
  }
  return context;
}